import React, { useState, useContext } from 'react';
import {
    TextField, Button, Paper, Typography, Container, Grid,
    FormControl, InputLabel, Select, MenuItem, Snackbar, Alert,
    Card, CardContent, CardMedia, Stack
} from '@mui/material';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import  db  from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FeedbackContext } from '../contexts/FeedbackContext';

const ManualArticleSubmission = () => {
    const [formData, setFormData] = useState({
        url: '',
        title: '',
        description: '',
        imageUrl: '',
        pubDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
        pubTime: new Date().toTimeString().slice(0, 5), // Current time in HH:MM format
    });
    const [loading, setLoading] = useState(false);
    const [previewMode, setPreviewMode] = useState(false);
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const { showFeedback } = useContext(FeedbackContext);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        const { url, title } = formData;
        if (!url.trim()) return 'URL is required';
        if (!title.trim()) return 'Title is required';

        // Basic URL validation
        try {
            new URL(url); // Will throw if URL is invalid
        } catch (e) {
            return 'Please enter a valid URL';
        }

        return null;
    };

    const handlePreview = () => {
        const error = validateForm();
        if (error) {
            showFeedback(error, 'error');
            return;
        }

        setPreviewMode(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const error = validateForm();
        if (error) {
            showFeedback(error, 'error');
            return;
        }

        setLoading(true);
        try {
            // Combine date and time into a single Date object
            const dateTimeStr = `${formData.pubDate}T${formData.pubTime}`;
            const pubDateTime = new Date(dateTimeStr);

            const articleData = {
                link: formData.url,
                TLDR: '',
                title: formData.title,
                description: formData.description || '',
                image: formData.imageUrl || null,
                pubDate: pubDateTime, // Use the combined date/time
                status: 'new',
                addedBy: currentUser?.email || 'anonymous',
                addedManually: true,
                createdAt: serverTimestamp(),
                vote: 0,
                insightfullCount: 0,
                article: {
                    description: formData.description || '',
                    title: formData.title,
                }
            };

            // Add console logs for debugging
            console.log('Adding article to database:', articleData);
            const docRef = await addDoc(collection(db, 'articles'), articleData);
            console.log('Document written with ID:', docRef.id);

            showFeedback('Article added successfully!', 'success');

            // Reset form
            setFormData({
                url: '',
                title: '',
                description: '',
                imageUrl: '',
                pubDate: new Date().toISOString().split('T')[0],
                pubTime: new Date().toTimeString().slice(0, 5),
            });
            setPreviewMode(false);
        } catch (error) {
            console.error('Error adding article:', error);
            showFeedback(`Failed to add article: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setPreviewMode(false);
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Add Article Manually
                </Typography>

                {!previewMode ? (
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    name="url"
                                    label="Article URL *"
                                    value={formData.url}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                    variant="outlined"
                                    placeholder="https://example.com/article"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    name="title"
                                    label="Article Title *"
                                    value={formData.title}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    name="description"
                                    label="Description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    fullWidth
                                    multiline
                                    rows={4}
                                    variant="outlined"
                                    placeholder="Enter article description or leave blank"
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField
                                        name="pubDate"
                                        label="Publication Date"
                                        type="date"
                                        value={formData.pubDate}
                                        onChange={handleChange}
                                        fullWidth
                                        variant="outlined"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                    <TextField
                                        name="pubTime"
                                        label="Time"
                                        type="time"
                                        value={formData.pubTime}
                                        onChange={handleChange}
                                        fullWidth
                                        variant="outlined"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        inputProps={{
                                            step: 300, // 5 min steps
                                        }}
                                    />
                                </Stack>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    name="imageUrl"
                                    label="Image URL"
                                    value={formData.imageUrl}
                                    onChange={handleChange}
                                    fullWidth
                                    variant="outlined"
                                    placeholder="https://example.com/image.jpg"
                                    helperText="Optional: URL for article image"
                                />
                            </Grid>

                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handlePreview}
                                    disabled={loading}
                                >
                                    Preview Article
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                ) : (
                    <div>
                        <Typography variant="h5" gutterBottom sx={{ mt: 3, mb: 2 }}>
                            Preview
                        </Typography>

                        <Card sx={{ mb: 3 }}>
                            {formData.imageUrl && (
                                <CardMedia
                                    component="img"
                                    height="200"
                                    image={formData.imageUrl}
                                    alt={formData.title}
                                    onError={(e) => {
                                        e.target.src = '/placeholder-image.jpg'; // Fallback image
                                        e.target.onerror = null;
                                    }}
                                />
                            )}

                            <CardContent>
                                <Typography variant="h6" component="div">
                                    {formData.title}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    URL: <a href={formData.url} target="_blank" rel="noopener noreferrer">{formData.url}</a>
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Publication Date: {
                                        new Date(`${formData.pubDate}T${formData.pubTime}`).toLocaleString(
                                            undefined,
                                            {
                                                dateStyle: 'medium',
                                                timeStyle: 'short'
                                            }
                                        )
                                    }
                                </Typography>

                                {formData.description && (
                                    <Typography variant="body1" sx={{ mt: 2 }}>
                                        {formData.description}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        <Grid container spacing={2} justifyContent="flex-end">
                            <Grid item>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancel}
                                >
                                    Edit
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                >
                                    {loading ? 'Saving...' : 'Save Article'}
                                </Button>
                            </Grid>
                        </Grid>
                    </div>
                )}
            </Paper>
        </Container>
    );
};

export default ManualArticleSubmission;